import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { ROUTES } from "constants/index";
import { WORK_ORDERS_URL } from "constants/urls";
import type { IMovementWorkOrderListItem } from "types";

interface FractionationOrdersContextValue {
  orders: IMovementWorkOrderListItem[] | null;
  loading: boolean;
  reloadOrders: () => Promise<IMovementWorkOrderListItem[] | null>;
}

const FractionationOrdersContext = createContext<FractionationOrdersContextValue | null>(null);

export const useFractionationOrders = () => {
  const context = useContext(FractionationOrdersContext);
  if (!context) throw new Error("FractionationOrdersProvider is required.");
  return context;
};

export default function FractionationOrdersProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const previousPath = useRef(pathname);
  const [orders, setOrders] = useState<IMovementWorkOrderListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const { showSnackBar } = useToast();
  const { fetchData } = useCrud<IMovementWorkOrderListItem>(WORK_ORDERS_URL, {
    disableAutoFetch: true,
  });
  const fetchOrders = useRef(fetchData);
  fetchOrders.current = fetchData;
  const pending = useRef<Promise<IMovementWorkOrderListItem[] | null> | null>(null);

  const reloadOrders = useCallback(() => {
    if (pending.current) return pending.current;
    const request = (async () => {
      setLoading(true);
      try {
        const response = await fetchOrders.current(undefined, true);
        if (response.status === 200 && Array.isArray(response.data)) {
          const loaded = response.data as IMovementWorkOrderListItem[];
          setOrders(loaded);
          return loaded;
        }
        showSnackBar(response.data?.message ?? "No se pudieron cargar las órdenes.", "error");
        return null;
      } catch {
        showSnackBar("No se pudieron cargar las órdenes.", "error");
        return null;
      } finally {
        setLoading(false);
        pending.current = null;
      }
    })();
    pending.current = request;
    return request;
  }, [showSnackBar]);

  useEffect(() => {
    void reloadOrders();
  }, [reloadOrders]);

  useEffect(() => {
    const returnedFromDetail =
      pathname === ROUTES.FRACTIONATION &&
      previousPath.current.startsWith(`${ROUTES.FRACTIONATION}/`);
    previousPath.current = pathname;
    if (returnedFromDetail) void reloadOrders();
  }, [pathname, reloadOrders]);

  return (
    <FractionationOrdersContext.Provider value={{ orders, loading, reloadOrders }}>
      {children}
    </FractionationOrdersContext.Provider>
  );
}
